import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";

const secondaryLineSx = {
  display: "block",
  fontSize: "0.8rem",
  color: "#7e8299",
};

const attemptCellSx = { width: 60 };
const whenCellSx = { width: 150 };

/**
 * Map a Stripe failure category to a Metronic label-light-* colour class.
 *
 * @param {string|null|undefined} category
 * @returns {string}
 */
const categoryColor = (category) => {
  switch (category) {
    case "card_error":
    case "issuer_declined":
      return "danger";
    case "invalid_request_error":
    case "invalid":
      return "warning";
    case "blocked":
      return "dark";
    case "api_error":
      return "info";
    default:
      return "secondary";
  }
};

/**
 * Compact failure-history table for the order detail screen. Renders one row
 * per recorded Stripe payment-failure attempt (newest first, capped at 20
 * server-side). Hidden entirely when the order has no recorded failures.
 *
 * @param {{ paymentFailures?: Array<object> }} props
 * @returns {JSX.Element|null} The payment failures table, or null when there are no failures.
 */
export const PaymentFailures = ({ paymentFailures }) => {
  if (!Array.isArray(paymentFailures) || paymentFailures.length === 0) {
    return null;
  }

  return (
    <div className="card card-custom gutter-b customer-duration">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label font-weight-bolder text-dark">
            Payment failures
          </span>
          <span className="text-muted mt-3 font-weight-bold font-size-sm">
            {paymentFailures.length} attempt
            {paymentFailures.length === 1 ? "" : "s"} recorded
          </span>
        </h3>
      </div>
      <div className="card-body pt-2">
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 350 }} aria-label="Payment failures table">
            <TableHead>
              <TableRow>
                <TableCell sx={attemptCellSx}>#</TableCell>
                <TableCell sx={whenCellSx}>When</TableCell>
                <TableCell>Failure code</TableCell>
                <TableCell>Decline reason</TableCell>
                <TableCell>Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentFailures.map((attempt) => {
                const message =
                  attempt.outcome_seller_message ||
                  attempt.failure_message ||
                  null;

                const tooltipBody = (
                  <div>
                    {message && (
                      <div style={{ marginBottom: 6 }}>{message}</div>
                    )}
                    <div>
                      <strong>Invoice:</strong>{" "}
                      {attempt.invoice_ref || "-"}
                    </div>
                    <div>
                      <strong>Charge:</strong>{" "}
                      {attempt.charge_id || "-"}
                    </div>
                    <div>
                      <strong>Payment intent:</strong>{" "}
                      {attempt.payment_intent_id || "-"}
                    </div>
                  </div>
                );

                return (
                  <Tooltip
                    key={attempt.id}
                    title={tooltipBody}
                    placement="top"
                    slotProps={{
                      tooltip: {
                        sx: { fontSize: "0.95rem", maxWidth: 360 },
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell sx={attemptCellSx}>
                        {attempt.attempt_number ?? "-"}
                      </TableCell>
                      <TableCell sx={whenCellSx}>
                        {attempt.failed_at
                          ? moment(attempt.failed_at).format(
                              "DD-MM-YYYY HH:mm"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {attempt.failure_code || "-"}
                        {attempt.decline_code && (
                          <span style={secondaryLineSx}>
                            {attempt.decline_code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {message || attempt.outcome_reason || "-"}
                        {attempt.outcome_reason && message && (
                          <span style={secondaryLineSx}>
                            {attempt.outcome_reason}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {attempt.failure_type || attempt.outcome_type ? (
                          <span
                            className={`label label-lg label-light-${categoryColor(
                              attempt.failure_type || attempt.outcome_type
                            )} label-inline`}
                          >
                            {attempt.failure_type ||
                              attempt.outcome_type}
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </Tooltip>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default PaymentFailures;
